import { useState } from 'react';

import {
  paths,
  NodeNames,
  tosaErrorCodes as errorCodes,
} from '@farmersdog/constants';
import { tosaToken } from '@farmersdog/lead-browser-storage';
import { navigateTo } from '@farmersdog/utils';

import { trackLandingPageEmailFormSubmitted } from '../../../analytics/events/trackLandingPageEmailFormSubmitted';
import { createLead } from '../../../graphql/createLead';
import { updateLead } from '../../../graphql/updateLead';
import { validateZipcode } from '../../../graphql/validateZipcode';
import { VALIDATION, getErrorMessage } from '../validation';

import type { ChangeEvent, FocusEventHandler, FormEventHandler } from 'react';

const INITIAL_FORM_STATE = {
  firstName: '',
  petsCount: 1,
  zip: '',
  email: '',
};

const INITIAL_VALIDATION_STATE = {
  zip: '',
  email: '',
};

export type ValidationFields = keyof typeof INITIAL_VALIDATION_STATE;

export type FormValues = typeof INITIAL_FORM_STATE;
export type ValidationMessages = typeof INITIAL_VALIDATION_STATE;

export function useEmailForm() {
  const [formValues, setFormValues] = useState<FormValues>(INITIAL_FORM_STATE);
  const [validationMessages, setValidationMessages] =
    useState<ValidationMessages>(INITIAL_VALIDATION_STATE);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<boolean>(false);

  const filled = Object.values(formValues).every(val => val);
  const valid = Object.values(validationMessages).every(val => !val);

  //  Me page will use petCount to prefill the petCount question.
  //  This is necessary since no pet entity is created by submitting this form.
  const mePageUrl = `${paths.PATH_SIGNUP_ME}/${formValues.email}?${NodeNames.NumPets}=${formValues.petsCount}`;

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const field = e.target.name as ValidationFields;
    const val = e.target.value;

    setValidationMessages({
      ...validationMessages,
      [field]: '',
    });

    setFormValues({ ...formValues, [field]: val });
  };

  /** Carry out client side pattern validation on blur */
  const handleBlur: FocusEventHandler<HTMLInputElement> = e => {
    const field = e.target.name as ValidationFields;
    const { patternMismatch } = e.target.validity;

    if (patternMismatch) {
      setValidationMessages({
        ...validationMessages,
        [field]: VALIDATION[field].pattern.message,
      });
    }
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const zipResponse = await validateZipcode({ zip: formValues.zip });

      if (!zipResponse?.data?.validateZipcode?.zipcode) {
        throw new Error(errorCodes.ZIP_NOT_SERVICEABLE);
      }

      const response = await createLead({ email: formValues.email });
      const token = response.data?.createLead.token;

      if (!token) {
        throw new Error(errorCodes.UNEXPECTED_ERROR);
      }

      tosaToken.set(token);

      await updateLead({
        firstName: formValues.firstName,
        zip: formValues.zip,
      });

      trackLandingPageEmailFormSubmitted(formValues);
      navigateTo(mePageUrl);
    } catch (error) {
      const message = getErrorMessage(error);

      switch (message) {
        case errorCodes.ZIP_INVALID:
        case errorCodes.ZIP_NOT_SERVICEABLE: {
          setValidationMessages({
            ...validationMessages,
            zip: VALIDATION.zip.invalid.message,
          });
          break;
        }

        default: {
          setSubmitError(true);
          break;
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return {
    formValues,
    validationMessages,
    completed: filled && valid,
    handleChange,
    handleBlur,
    handleSubmit,
    submitError,
    submitting,
  };
}
